// La Loge, espace client d'ACTE.5 : règles pures partagées par le serveur, l'application client et World.
// Aucune dépendance au navigateur ni à Cloudflare : tout ici se teste à l'horloge figée (tests/unit/loge.test.ts).
// Contrat des échanges : docs/LOGE-API.md.

export type LogeLang = 'fr' | 'en';
export type LogeActor = 'client' | 'studio' | 'system';
export type DecisionAction = 'approve' | 'note' | 'call';
export type ProposalStatus = 'open' | 'approved' | 'discussing' | 'replaced' | 'withdrawn';

// ── Modèles de projet ─────────────────────────────────────────────────────────────────────────────

export interface StepDef { fr: string; en: string }
/** Sous-dossier créé d'office dans une étape (nom gardé en français en base, traduit chez le client). */
export interface SubfolderDef { step: number; fr: string; en: string }
export interface ProjectTemplate { key: string; fr: string; en: string; steps: StepDef[]; subfolders?: SubfolderDef[] }

/** Étape figée dans un projet (jsonb `loge_projects.steps`). `plan_*` : fourchette affichée au client (phase 2). */
export interface ProjectStep extends StepDef { plan_fr: string; plan_en: string; approved_at: string | null }

export const TEMPLATES: Record<string, ProjectTemplate> = {
  montre: {
    key: 'montre', fr: 'Montre', en: 'Watch',
    steps: [
      { fr: 'Brief et direction', en: 'Brief and direction' },
      { fr: 'Design', en: 'Design' },
      { fr: '3D, prototype résine et rendu', en: '3D, resin prototype and renders' },
      { fr: 'Dossier technique', en: 'Technical file' },
      { fr: 'Usine et échantillons', en: 'Factory and samples' },
      { fr: 'Validation de la présérie', en: 'Pre-series approval' },
    ],
    // Les rendus KeyShot, fixes ou à 360°, vivent dans l'étape 03 elle-même : son nom les annonce,
    // un sous-dossier séparé n'apportait qu'un clic de plus.
  },
};

export const CLIENT_FOLDER = { fr: 'Vos envois', en: 'Your uploads' } as const;
/**
 * Dossier « 00 · Contrat et factures » : l'accord de confidentialité, le devis signé, l'offre et les factures.
 * Ce n'est pas une étape du projet — il ne figure donc pas dans `steps` ni sur la frise — mais il porte le
 * numéro 0 pour se ranger avant « 01 », là où les dossiers sans étape tombent en fin de liste.
 */
export const ADMIN_FOLDER = { step: 0, fr: 'Contrat et factures', en: 'Contract and invoices' } as const;
/** Dossier caché de la couverture du projet (kind 'cover') : le client n'en voit que l'image. */
export const COVER_FOLDER = { name: 'Couverture', position: 100 } as const;

export function templateSteps(key: string): ProjectStep[] {
  const t = TEMPLATES[key] ?? TEMPLATES.montre!;
  return t.steps.map((s) => ({ ...s, plan_fr: '', plan_en: '', approved_at: null }));
}

/** Dossiers créés avec un projet : un par étape (« 01 · Brief et direction »), plus « Vos envois » pour le client. */
export function defaultFolders(steps: StepDef[]): Array<{ step: number | null; name: string; kind: 'studio' | 'client'; position: number }> {
  return [
    { step: ADMIN_FOLDER.step, name: `${pad2(0)} · ${ADMIN_FOLDER.fr}`, kind: 'studio' as const, position: 0 },
    ...steps.map((s, i) => ({ step: i + 1, name: `${pad2(i + 1)} · ${s.fr}`, kind: 'studio' as const, position: i + 1 })),
    { step: null, name: CLIENT_FOLDER.fr, kind: 'client' as const, position: 99 },
  ];
}

/**
 * Sous-dossiers créés avec un projet, à ranger sous le dossier de leur étape (`parent_id` = dossier de l'étape `step`).
 * Placés après les dossiers d'étape (position 30 et suivantes), avant les dossiers libres ajoutés ensuite.
 */
export function defaultSubfolders(key: string): Array<{ step: number; name: string; kind: 'studio'; position: number }> {
  const t = TEMPLATES[key] ?? TEMPLATES.montre!;
  return (t.subfolders ?? []).map((f, i) => ({ step: f.step, name: f.fr, kind: 'studio' as const, position: 30 + i + 1 }));
}

/**
 * Dossiers que le studio voit : un dossier retiré disparaît de l'arborescence, sauf s'il garde encore
 * des fichiers — sinon ils deviendraient inatteignables, même depuis la corbeille.
 */
export function visibleFolders<F extends { id: string; deletedAt: string | null }>(folders: F[], files: Array<{ folderId: string }>): F[] {
  const holding = new Set(files.map((f) => f.folderId));
  return folders.filter((f) => !f.deletedAt || holding.has(f.id));
}

/** Nom d'un dossier dans la langue du lecteur : les sous-dossiers des modèles sont traduits, les autres restent tels quels. */
export function folderLabel(name: string, lang: LogeLang): string {
  if (lang !== 'en') return name;
  const key = name.trim().toLowerCase();
  for (const t of Object.values(TEMPLATES)) {
    const hit = t.subfolders?.find((f) => f.fr.toLowerCase() === key);
    if (hit) return hit.en;
  }
  return name;
}

export const pad2 = (n: number): string => String(n).padStart(2, '0');

export function stepLabel(step: StepDef, lang: LogeLang): string {
  return lang === 'en' ? step.en : step.fr;
}

/** État d'une étape vu par le client : faite, en cours, à venir. Jamais « en retard ». */
export function stepState(index1: number, current: number): 'done' | 'now' | 'todo' {
  return index1 < current ? 'done' : index1 === current ? 'now' : 'todo';
}

// ── Fichiers et versions ──────────────────────────────────────────────────────────────────────────

/** Base d'un nom de fichier, sans extension ni marque de version : « Heritage38_rendus_V2.pdf » → « heritage38_rendus ». */
export function baseName(name: string): string {
  const noExt = name.replace(/\.[a-z0-9]{1,5}$/i, '');
  return noExt
    .replace(/[\s_.-]*(?:v|version|rev)[\s_.-]*\d{1,3}$/i, '')
    .trim()
    .toLowerCase();
}

/** Version suivante d'un document dans un dossier, d'après les fichiers déjà présents (corbeille comprise). */
export function nextVersion(existing: Array<{ base_name: string; version: number }>, name: string): number {
  const b = baseName(name);
  return existing.filter((f) => f.base_name === b).reduce((m, f) => Math.max(m, f.version), 0) + 1;
}

export type FileFamily = 'image' | 'pdf' | 'video' | '3d' | 'archive' | 'other';

export function fileFamily(mime: string, name: string): FileFamily {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.startsWith('video/')) return 'video';
  if (['step', 'stp', 'glb', 'gltf', 'obj', 'stl', '3dm', 'iges', 'igs'].includes(ext)) return '3d';
  if (['zip', '7z', 'rar'].includes(ext)) return 'archive';
  return 'other';
}

/** Fichiers dont World tire une maquette 3D de consultation : STEP et IGES (convertis), GLB (tel quel). */
export type ModelSource = 'step' | 'iges' | 'glb';

/** Un .gltf à fichiers séparés, un .3dm, un OBJ ou un STL partent sans maquette (src/core/loge3d.ts pour la suite). */
export function modelSource(mime: string, name: string): ModelSource | null {
  const ext = (name.toLowerCase().split('.').pop() ?? '').trim();
  if (ext === 'step' || ext === 'stp') return 'step';
  if (ext === 'iges' || ext === 'igs') return 'iges';
  if (ext === 'glb' || mime.toLowerCase() === 'model/gltf-binary') return 'glb';
  return null;
}

/** Étiquette courte affichée sur une vignette (« PDF », « STEP »…). */
export function fileTag(name: string): string {
  const ext = (name.split('.').pop() ?? '').toUpperCase();
  return ext.length >= 2 && ext.length <= 5 ? ext : 'FICHIER';
}

/** Nom de fichier sûr pour une clé R2. */
export function safeKeyName(name: string): string {
  const n = name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-+/g, '-');
  return n.replace(/-+\./g, '.').replace(/^[-.]+|[-.]+$/g, '').slice(0, 100) || 'fichier';
}

/** Clés R2 : un original et ses aperçus par fichier, rangés par projet. */
export function originalKey(projectId: string, fileId: string, name: string): string {
  return `p/${projectId}/${fileId}/original/${safeKeyName(name)}`;
}
export function previewKey(projectId: string, fileId: string, page: number): string {
  return `p/${projectId}/${fileId}/preview/${page}.jpg`;
}
/** Maquette 3D de consultation (GLB allégé, fabriqué par le navigateur du studio), rangée avec les aperçus. */
export function modelKey(projectId: string, fileId: string): string {
  return `p/${projectId}/${fileId}/preview/model.glb`;
}

/** Morceaux d'envoi : les fonctions Cloudflare refusent les corps de plus de 100 Mo. */
export const CHUNK_BYTES = 50 * 1024 * 1024;
export const MAX_STUDIO_FILE_BYTES = 5 * 1024 * 1024 * 1024;
export const MAX_CLIENT_FILE_BYTES = 200 * 1024 * 1024;
/** Plus grand côté d'un aperçu : une planche A4 reste nette quand le client zoome (environ 270 dpi). */
export const PREVIEW_MAX_PX = 3200;

export function chunkCount(size: number): number {
  return Math.max(1, Math.ceil(size / CHUNK_BYTES));
}

/** Texte du filigrane des aperçus : la marque du studio (retour du pilote : pas le nom du client répété partout). */
export function watermarkText(lang: LogeLang): string {
  return `ACTE·5 · ${lang === 'en' ? 'Confidential' : 'Confidentiel'}`;
}

// ── Accès et téléchargements ──────────────────────────────────────────────────────────────────────

export interface MemberAccess { client_id: string; project_ids: string[] | null; revoked_at: string | null }

export function canSeeProject(m: MemberAccess, project: { id: string; client_id: string }): boolean {
  if (m.revoked_at) return false;
  if (m.client_id !== project.client_id) return false;
  return m.project_ids === null || m.project_ids.includes(project.id);
}

/** Après la fin d'un projet, le client garde son accès six mois (le temps de télécharger ses fichiers), puis il se ferme. */
export const ACCESS_AFTER_COMPLETION_MONTHS = 6;

export interface ProjectAccess { status: 'active' | 'completed' | 'archived'; completed_at?: string | null }

/** Fin de l'accès du client à un projet terminé (ISO), ou null tant que le projet n'est pas terminé. */
export function accessEndsAt(p: ProjectAccess): string | null {
  if (p.status !== 'completed' || !p.completed_at) return null;
  const d = new Date(p.completed_at);
  d.setUTCMonth(d.getUTCMonth() + ACCESS_AFTER_COMPLETION_MONTHS);
  return d.toISOString();
}

/** Projet encore ouvert au client : en cours, ou terminé depuis moins de six mois. Archivé : fermé tout de suite. */
export function projectOpen(p: ProjectAccess, now = Date.now()): boolean {
  if (p.status === 'archived') return false;
  const end = accessEndsAt(p);
  return !end || Date.parse(end) > now;
}

/** Téléchargement de l'original : fichier envoyé par le client lui-même, exception posée par le studio, ou projet débloqué. */
export function canDownload(file: { downloadable: boolean; uploaded_by: 'studio' | 'client' }, project: { downloads_unlocked: boolean }, client: { downloads_unlocked: boolean }): boolean {
  return file.uploaded_by === 'client' || file.downloadable || project.downloads_unlocked || client.downloads_unlocked;
}

// ── Propositions, rounds et décisions ─────────────────────────────────────────────────────────────

/**
 * Round d'une nouvelle proposition : le suivant de la dernière proposition de la même étape dans le même dossier,
 * si celle-ci a été discutée (note ou appel). Sinon 1 (nouveau sujet ou étape précédente validée).
 */
export function nextRound(previous: Array<{ step: number; folder_id: string | null; round: number; status: ProposalStatus; created_at: string }>, step: number, folderId: string | null): number {
  // Une demande retirée n'a jamais compté pour le client : elle ne fait pas avancer le round.
  const same = previous.filter((p) => p.step === step && p.folder_id === folderId && p.status !== 'withdrawn').sort((a, b) => a.created_at.localeCompare(b.created_at));
  const last = same[same.length - 1];
  if (!last) return 1;
  return last.status === 'discussing' || last.status === 'replaced' || last.status === 'open' ? last.round + 1 : 1;
}

export const NOTE_MAX = 600;

export function cleanNote(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.replace(/\r\n/g, '\n').replace(/[ \t]+/g, ' ').trim();
  return t.length >= 1 && t.length <= NOTE_MAX ? t : null;
}

export function isDecisionAction(v: unknown): v is DecisionAction {
  return v === 'approve' || v === 'note' || v === 'call';
}

/** Statut d'une proposition après une décision. Une note seule laisse la décision ouverte. */
export function statusAfter(action: DecisionAction): ProposalStatus {
  return action === 'approve' ? 'approved' : action === 'call' ? 'discussing' : 'open';
}

/** Demande encore à décider par le client (ouverte, ou en discussion après une demande d'appel). */
export const isWaiting = (status: ProposalStatus): boolean => status === 'open' || status === 'discussing';

/** Une décision ne se demande que sur l'étape en cours : avancer l'étape d'abord, à la validation ou à la main. */
export const canRequestDecision = (folderStep: number | null, currentStep: number): boolean => folderStep !== null && folderStep === currentStep;

/**
 * Demandes à retirer d'elles-mêmes : encore à décider, et dont plus aucun fichier n'est hors de la corbeille.
 * `live` : identifiants des fichiers prêts et hors corbeille du projet.
 */
export function proposalsLeftEmpty<P extends { id: string; status: ProposalStatus; file_ids: string[] }>(proposals: P[], live: Set<string>): P[] {
  return proposals.filter((p) => isWaiting(p.status) && !p.file_ids.some((id) => live.has(id)));
}

/** Étape en cours après la validation d'une proposition (jamais en arrière, jamais au-delà de la dernière). */
export function stepAfterApproval(current: number, proposalStep: number, advances: boolean, stepCount: number): number {
  if (!advances) return current;
  return Math.min(stepCount, Math.max(current, proposalStep + 1));
}

// ── Connexion ────────────────────────────────────────────────────────────────────────────────────

export const LOGIN_TTL_MS = 15 * 60_000;
export const SESSION_TTL_MS = 30 * 24 * 3600_000;
export const LOGIN_PER_HOUR = 5;
export const SESSION_COOKIE = 'loge_s';

export function cleanEmail(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const e = v.trim().toLowerCase();
  return e.length <= 120 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e) ? e : null;
}

export function cleanName(v: unknown, max = 120): string | null {
  if (typeof v !== 'string') return null;
  const t = v.replace(/\s+/g, ' ').trim();
  return t.length >= 1 && t.length <= max ? t : null;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const s = parts.length >= 2 ? parts[0]![0]! + parts[parts.length - 1]![0]! : (parts[0] ?? '?').slice(0, 2);
  return s.toUpperCase();
}

/** Adresse de La Loge (production) ; l'environnement de test passe la sienne. */
export const LOGE_ORIGIN = 'https://loge.acte-5.com';

export function isLogeHost(host: string): boolean {
  const h = host.toLowerCase().split(':')[0] ?? '';
  return h === 'loge.acte-5.com' || h.startsWith('loge.') || h.startsWith('loge-');
}
